/**
 * Session identity — cookie-free, tab-scoped, deliberately forgetful.
 *
 * A UUID in `sessionStorage`. Not a cookie, not `localStorage`, and never a
 * hash of IP or user agent. That last exclusion is the important one: a
 * derived "anonymous" identifier is fingerprinting, it is worse for privacy
 * than the cookie it replaces, and it degrades into nonsense behind NAT and
 * private relays anyway.
 *
 * The honest consequence, which the dashboards state alongside the numbers:
 * we can count **sessions** and we can count **signed-in accounts**. We
 * cannot count returning anonymous visitors, and "unique visitors" is not a
 * number this site can produce. A new tab is a new session. Changing that
 * requires a consent banner, which is a product decision — see the "No
 * cookie banner" entry in docs/backlog.md.
 *
 * @module logging/core/session
 */

import { diagnostic } from './diagnostic';
import { readStored, readStoredNumber, writeStored } from './storage';
import { boundedDwellMs, resetSessionVisibleMs, sessionVisibleMs } from './visible-clock';

/** Tab-scoped keys. Namespaced so they are obvious in devtools. */
const SESSION_ID_KEY = 'homeceo_log_session_id';
const SEQUENCE_KEY = 'homeceo_log_sequence';

// =============================================================================
// IDENTIFIERS
// =============================================================================

/**
 * A UUID, preferring the platform generator.
 *
 * `crypto.randomUUID` requires a secure context. That covers production
 * (https) and localhost, but not a bare-IP dev host on the LAN, where it is
 * simply undefined. The fallback is not cryptographically strong and does
 * not need to be — this identifier is a grouping key for a tab's events,
 * not a secret or a capability.
 */
function generateUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the non-cryptographic path.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** True when this page load minted the session, rather than joining one
 *  already in progress. Drives the once-per-session `web_session_started`. */
let mintedThisLoad = false;

/**
 * The current tab's session id, minting one on first call.
 */
export function getSessionId(): string {
  const existing = readStored(SESSION_ID_KEY);
  if (existing) return existing;

  const id = generateUuid();
  writeStored(SESSION_ID_KEY, id);
  writeStored(SEQUENCE_KEY, '0');
  // A fresh session must not inherit an earlier one's accumulated clock.
  resetSessionVisibleMs();
  mintedThisLoad = true;
  diagnostic('minted session', id);
  return id;
}

/** Whether `getSessionId()` created the session during this page load. */
export function isNewSession(): boolean {
  getSessionId();
  return mintedThisLoad;
}

// =============================================================================
// DURATION
// =============================================================================

/**
 * Engaged session duration: visible milliseconds since the session began.
 *
 * **Not wall-clock**, and the name says so because the distinction changes
 * what the number means. `web_session_ended` fires on every
 * `visibilitychange -> hidden`, so a tab reopened after a weekend would
 * report a multi-day wall-clock "session" — a single such row dominates any
 * average it lands in. Counting only visible time makes the field robust to
 * that, and makes it comparable with the per-page dwell reported on
 * `web_page_viewed`, which is measured the same way.
 *
 * Survives reloads inside the tab; bounded by `config.maxDwellMs`.
 */
export function getSessionEngagedMs(): number {
  getSessionId();
  return boundedDwellMs(sessionVisibleMs());
}

// =============================================================================
// SEQUENCE
// =============================================================================

/**
 * The next per-session sequence number, starting at 1.
 *
 * Persisted rather than held in a module variable because a reload inside
 * the same tab keeps the session id but would reset an in-memory counter —
 * producing two events numbered 1 in one session, which turns the gap
 * detection this field exists for into a lie. A `sessionStorage` write per
 * event is affordable at this site's event volume.
 */
export function nextSequence(): number {
  getSessionId();
  const next = readStoredNumber(SEQUENCE_KEY) + 1;
  writeStored(SEQUENCE_KEY, String(next));
  return next;
}
